import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { UserActions } from '@recruitee/account-data';
import type { Candidate } from '@recruitee/candidate-types';
import { createEffectFactory, referencesToActionsFactory } from '@recruitee/ngrx';
import { JobsBriefActions, StageActions } from '@recruitee/offers-data';
import { PlacementActions } from '@recruitee/placements-data';

import { CandidateHttpClient, CandidateReferences } from './candidate-http-client';
import { CandidateActions } from './candidate.store';

@Injectable({ providedIn: 'root' })
export class CandidateEffects {
  public effect = createEffectFactory(CandidateActions, this.actions$);

  public fetchCandidates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CandidateActions.fetchCollection),
      mergeMap(({ payload }) =>
        this.candidateHttpClient.loadCollection(payload.params).pipe(
          switchMap(({ candidates, references }) => [
            ...this.getReferencesActions(candidates, references),
            CandidateActions.fetchCollectionSuccess({
              payload: { data: candidates, scope: payload.scope },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(CandidateActions.fetchCollectionFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public fetchCandidateRating$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CandidateActions.fetchCandidateRating),
      mergeMap(({ payload }) =>
        this.candidateHttpClient.fetchCandidateRating(payload.id).pipe(
          switchMap(data => [CandidateActions.fetchCandidateRatingSuccess({ payload: data })]),
          catchError((error: HttpErrorResponse) =>
            of(CandidateActions.fetchCandidateRatingFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private candidateHttpClient: CandidateHttpClient,
  ) {}

  private getReferencesActions(candidates: Candidate[], references: CandidateReferences[]) {
    const referenceActions = referencesToActionsFactory<CandidateReferences>()([
      { type: 'Admin', action: UserActions.references },
      { type: 'Stage', action: StageActions.references },
      { type: 'Offer', action: JobsBriefActions.references },
    ]);

    const placementActions = candidates.map(candidate =>
      PlacementActions.references({
        payload: { data: candidate.placements },
      }),
    );

    return [...referenceActions(references), ...placementActions];
  }
}
